/**
 * O que a página faz com as vendas assinadas: quem pode o quê, as sugestões
 * de ligação e qual painel (registrar, ligar, cancelar) está aberto. Fica
 * fora do `index.jsx` para a página só montar as peças.
 */
import { useCallback, useMemo, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { PERM_LIGAR_VENDA, PERM_REGISTRAR_VENDA } from "../../hooks/useVendasAssinadas";
import { sugestoesDeLigacao } from "../../utils/capacidade";
import { formularioDaSimulacao, formularioVazio } from "./apresentacaoVendas";

const SEM_SUGESTOES = Object.freeze({});
const FECHADO = Object.freeze({ aberto: false, form: null, daSimulacao: false });

/**
 * Cancelar: o PCP sempre; o comercial só a venda que ele registrou. A lista
 * traz o NOME de quem registrou (não o id), então a tela compara pelo nome
 * e o backend confere de verdade (403 com o motivo).
 */
function registradaPor(venda, usuario) {
  const quem = String(venda?.registrada_por || "").trim();
  return Boolean(quem) && [usuario?.nome, usuario?.email].some((nome) => nome && String(nome).trim() === quem);
}

/**
 * `{sugestoes, quantasSugeridas, podeRegistrar, podeLigar, podeCancelarDe,
 * registro, abrirRegistro, registrarDaSimulacao, fecharRegistro, registrar,
 * ligando, setLigando, cancelando, setCancelando}`.
 * - `vendas` = `useVendasAssinadas()`; `linhas` = linhas classificadas da planilha;
 * - `aoRegistrarDaSimulacao` = o que fazer depois de registrar uma venda que
 *   veio da simulação (limpar a simulação: senão a mesma venda conta duas vezes).
 */
export default function useVendasNaPagina({ vendas, linhas, hoje, aoRegistrarDaSimulacao }) {
  const { hasPermission, usuario } = useAuth() || {};
  const podeRegistrar = Boolean(vendas.podeRegistrar || hasPermission?.(PERM_REGISTRAR_VENDA));
  const podeLigar = Boolean(vendas.podeLigar || hasPermission?.(PERM_LIGAR_VENDA));

  const sugestoes = useMemo(
    () => (linhas ? sugestoesDeLigacao({ vendas: vendas.vendas, linhas }) : SEM_SUGESTOES),
    [vendas.vendas, linhas],
  );

  const podeCancelarDe = useCallback(
    (venda) => podeLigar || (podeRegistrar && registradaPor(venda, usuario)),
    [podeLigar, podeRegistrar, usuario],
  );

  const [registro, setRegistro] = useState(FECHADO);
  const abrirRegistro = useCallback(() => setRegistro({ aberto: true, form: formularioVazio(hoje), daSimulacao: false }), [hoje]);
  const registrarDaSimulacao = useCallback(
    (entrada) => setRegistro({ aberto: true, form: formularioDaSimulacao(entrada, hoje), daSimulacao: true }),
    [hoje],
  );
  const fecharRegistro = useCallback(() => setRegistro((atual) => ({ ...atual, aberto: false })), []);
  const { registrar: registrarNoBackend } = vendas;
  const registrar = useCallback(
    async (corpo) => {
      const venda = await registrarNoBackend(corpo);
      if (registro.daSimulacao) aoRegistrarDaSimulacao?.();
      return venda;
    },
    [registrarNoBackend, registro.daSimulacao, aoRegistrarDaSimulacao],
  );

  const [ligando, setLigando] = useState(null);
  const [cancelando, setCancelando] = useState(null);

  return {
    sugestoes,
    quantasSugeridas: Object.keys(sugestoes).length,
    podeRegistrar,
    podeLigar,
    podeCancelarDe,
    registro,
    abrirRegistro,
    registrarDaSimulacao,
    fecharRegistro,
    registrar,
    ligando,
    setLigando,
    cancelando,
    setCancelando,
  };
}
